import { CommonModule } from "@angular/common";
import { Component, computed, inject, model, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import {
    DocumentService,
    DocumentTemplateCategoryService,
    DocumentTemplateDto,
    DocumentTemplateService,
    FixDocumentService,
} from "@malirang/api-client";
import {
    DocumentEtcType,
    DocumentEtcTypeKey,
    DocumentListCategory,
} from "@malirang/common";
import {
    Button,
    Input,
    ModalAdapter,
    ModalContainer,
    ModalHeader,
} from "@malirang/front-share";
import { lastValueFrom, map } from "rxjs";
import { CenterStore } from "../../../../../stores/center/center.store";
import { DocumentStore } from "../../store/document.store";
import {
    AddDocumentTemplateModal,
    AddDocumentTemplateModalProps,
} from "../add-document-template/add-document-template.modal";
import {
    ManageDocumentCategoryModal,
    ManageDocumentCategoryModalProps,
} from "../manage-document-category/manage-document-category.modal";
import { DocumentTemplateItemComponent } from "./document-template-item/document-template-item.component";

export type AddDocumentModalProps = {
    ticketId: string;
};

@Component({
    selector: "app-add-document",
    templateUrl: "./add-document.modal.html",
    styleUrls: ["./add-document.modal.css"],
    imports: [
        CommonModule,
        ModalContainer,
        ModalHeader,
        Button,
        Input,
        DocumentTemplateItemComponent,
    ],
})
export class AddDocumentModal extends ModalAdapter<AddDocumentModalProps> {
    readonly documentTemplateService = inject(DocumentTemplateService);
    readonly categoryService = inject(DocumentTemplateCategoryService);
    readonly centerStore = inject(CenterStore);
    readonly documentService = inject(DocumentService);
    readonly fixDocumentService = inject(FixDocumentService);
    readonly documentStore = inject(DocumentStore);

    query = model<string>("");
    cursor = model<string>("");
    haxNext = model(false);
    categoryId = model<string | null>(null);
    items = signal<DocumentTemplateDto[]>([]);
    ticketId = computed(() => this.props()?.ticketId);

    templateIds = model<string[]>([]);

    totalTemplateCount = computed(
        () =>
            this.categories()?.reduce((acc, category) => {
                return acc + (category.templateCount || 0);
            }, 0) || 0,
    );

    fixDocumentTypes = signal<DocumentEtcTypeKey[]>([]);
    fixDocumentCategory = signal<string>("");
    etcDocuments = [DocumentEtcType.변경계약서, DocumentEtcType.사유서];

    $items = rxResource({
        stream: () =>
            this.documentTemplateService
                .documentTemplateControllerSearchCursor({
                    query: this.query(),
                    cursor: this.cursor(),
                    pageSize: 5,
                    categoryId: this.categoryId() || "",
                    centerId: this.centerStore.currentCenterId(),
                })
                .pipe(
                    map((res) => {
                        if (res) {
                            const items =
                                res.items as unknown as DocumentTemplateDto[];
                            this.haxNext.set(res.hasNext);
                            this.cursor.set(res.nextCursor || "");
                            this.items.set([...this.items(), ...items]);
                            return res.items;
                        }

                        return [];
                    }),
                ),
    });

    $categories = rxResource({
        stream: ({ params }) =>
            this.categoryService.documentTemplateCategoryControllerFindAll({
                centerId: params.centerId,
            }),
        params: () => ({
            centerId: this.centerStore.currentCenterId(),
        }),
    });
    categories = computed(() => {
        if (this.$categories.hasValue()) {
            return this.$categories.value();
        }

        return [];
    });

    isAll = computed(() => {
        return !this.categoryId() && this.fixDocumentCategory() === "";
    });

    handleAll() {
        this.fixDocumentCategory.set("");
        this.categoryId.set(null);
        this.templateIds.set([]);
        this.handleChange();
    }

    handleChange() {
        this.items.set([]);
        this.cursor.set("");
        this.haxNext.set(false);
        this.$items.reload();
    }

    handleCategoryClick(categoryId: string) {
        this.templateIds.set([]);
        this.fixDocumentCategory.set("");

        if (this.categoryId() === categoryId) {
            this.categoryId.set(null);
        } else {
            this.categoryId.set(categoryId);
        }

        this.handleChange();
    }

    handleFixCategoryClick(category: string) {
        this.fixDocumentCategory.set(category);
        this.categoryId.set(null);
        this.templateIds.set([]);
        this.handleChange();
    }

    handleFixDocumentClick(type: DocumentEtcTypeKey) {
        if (this.fixDocumentTypes().includes(type)) {
            this.fixDocumentTypes.update((types) =>
                types.filter((t) => t !== type),
            );
        } else {
            this.fixDocumentTypes.update((types) => [...types, type]);
        }
    }

    handleManageCategory() {
        this.modalService
            .create<ManageDocumentCategoryModalProps>(
                ManageDocumentCategoryModal,
                {
                    componentProps: {},
                },
            )
            .closed.subscribe(() => {
                this.$categories.reload();
            });
    }

    handleAddDocumentTemplate() {
        const modal = this.modalService.create<AddDocumentTemplateModalProps>(
            AddDocumentTemplateModal,
            {
                componentProps: {},
            },
        );

        modal.closed.subscribe((result) => {
            if (result) {
                this.handleRefresh();
            }
        });
    }

    handleRefresh() {
        this.handleChange();
        this.$categories.reload();
    }

    handleTemplateClick(item: DocumentTemplateDto) {
        if (this.templateIds().includes(item.id)) {
            this.templateIds.update((ids) =>
                ids.filter((id) => id !== item.id),
            );
        } else {
            this.templateIds.update((ids) => [...ids, item.id]);
        }
    }

    async addTemplate() {
        if (!this.templateIds().length && !this.fixDocumentTypes().length) {
            return;
        }

        try {
            if (this.templateIds()?.length) {
                await lastValueFrom(
                    this.documentService.documentControllerCreateDocument({
                        body: {
                            templateIds: this.templateIds(),
                            ticketId: this.ticketId()!,
                        },
                        centerId: this.centerStore.currentCenterId(),
                    }),
                );
            }

            if (this.fixDocumentTypes()?.length) {
                await lastValueFrom(
                    this.fixDocumentService.fixDocumentControllerAddEtcDocument(
                        {
                            body: {
                                ticketId: this.ticketId()!,
                                types: this.fixDocumentTypes(),
                            },
                            centerId: this.centerStore.currentCenterId(),
                        },
                    ),
                );
            }

            this.toastService.success("문서가 성공적으로 생성되었습니다.");
            this.modalService.close();
            this.documentStore.reload$.next();
        } catch (error) {
            this.toastService.error("이미 등록된 템플릿 입니다.");
        }
    }
}
